import React, { useEffect, useState } from 'react';
import useApiUrlTranslation from './useApiUrlTranslation';

// Bandeau « Export Profile » — 6 blocs : VERTICAL · COUNTRY BASED · EXPORT TARGET ·
// COORDINATED BY · COHORT · EXPORT POTENTIAL.
// Logique source : screen_manager/content/contact/index.dev.php + CContact.class.php
// (targetedCountries + maturitePotentielAccelerationInternational).

// Mapping statuts auto_eveluation → niveau (CContact::maturitePotentielAccelerationInternational)
const LEVEL_BY_STATUT = { 161: 0, 113: 1, 112: 2, 111: 3 };

const DEFAULT_COHORT = {
    label: 'HEALTHTECH EVERYWHERE',
    image: null, // TODO : source dynamique de la cohorte à définir (réflexion en cours)
};

// Globe SVG inline (repris du bloc Targeted Countries de index.dev.php)
const GlobeSvg = () => (
    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none" stroke="white" aria-hidden="true">
        <circle cx="100" cy="100" r="85" strokeWidth="4" opacity="0.25" />
        <ellipse cx="100" cy="100" rx="85" ry="30" strokeWidth="2" opacity="0.2" />
        <ellipse cx="100" cy="55" rx="70" ry="10" strokeWidth="1.5" opacity="0.15" />
        <ellipse cx="100" cy="145" rx="70" ry="10" strokeWidth="1.5" opacity="0.15" />
        <ellipse cx="100" cy="100" rx="40" ry="85" strokeWidth="2.5" opacity="0.2" />
        <ellipse cx="100" cy="100" rx="65" ry="85" strokeWidth="1.5" opacity="0.15" />
    </svg>
);

// Badge Level : 2 cercles + 4 arcs fléchés orbitaux (repris du bloc Export Expertise de index.dev.php)
const LevelBadgeSvg = () => (
    <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" fill="none" aria-hidden="true">
        <circle cx="100" cy="100" r="92" stroke="white" strokeWidth="1.5" opacity="0.15" />
        <circle cx="100" cy="100" r="58" stroke="white" strokeWidth="1.5" opacity="0.15" />
        {[0, 90, 180, 270].map(angle => (
            <g key={angle} transform={`rotate(${angle} 100 100)`}>
                <path d="M107,20.3 A80,80 0 0,1 178.8,86.1" stroke="white" strokeWidth="6" strokeLinecap="round" />
                <polygon points="170.3,70.3 178.8,86.1 182.1,72.3" fill="white" />
            </g>
        ))}
    </svg>
);

const BannerBlock = ({ label, children }) => (
    <div className="flex flex-col items-center gap-2 px-3 py-4 border-white/20 [&:nth-child(n+2)]:border-l">
        <div className="text-xs font-black tracking-widest uppercase text-center">{label}</div>
        <div className="flex-1 flex flex-col items-center justify-center gap-1">{children}</div>
    </div>
);

const ContactProfileBanner = ({ idContact, cohort = DEFAULT_COHORT }) => {

    const { apiUrlTranslation, i18n } = useApiUrlTranslation();

    const [contact, setContact] = useState(null);
    const [contactCycleLangList, setContactCycleLangList] = useState(null);
    const [exportLevel, setExportLevel] = useState(0);
    const [coordinateur, setCoordinateur] = useState(null);

    useEffect(() => {
        if (!idContact) return;
        const controller = new AbortController();

        fetch(`${apiUrlTranslation}?action=getContact&id_contact=${idContact}`, { signal: controller.signal })
            .then(res => res.json())
            .then(res => setContact(res))
            .catch(err => { if (err.name !== 'AbortError') console.error(err); });

        fetch(`${apiUrlTranslation}?action=getContactCycleLang&filter=id_contact=${idContact}&get_parents=0&get_children=1&source=contactprofilebanner`, { signal: controller.signal })
            .then(res => res.json())
            .then(res => setContactCycleLangList(Array.isArray(res) ? res : []))
            .catch(err => { if (err.name !== 'AbortError') console.error(err); });

        fetch(`${apiUrlTranslation}?action=getContactStatutList&filter= WHERE id_contact=${idContact}`, { signal: controller.signal })
            .then(res => res.json())
            .then(res => {
                // Réplique de CContact::maturitePotentielAccelerationInternational (161 court-circuite à 0)
                let note = 0;
                for (const statut of (Array.isArray(res) ? res : [])) {
                    if (statut.id_statut == 161) { note = 0; break; }
                    if (LEVEL_BY_STATUT[statut.id_statut] !== undefined) note = LEVEL_BY_STATUT[statut.id_statut];
                }
                setExportLevel(note);
            })
            .catch(err => { if (err.name !== 'AbortError') console.error(err); });

        return () => controller.abort();
    }, [idContact, apiUrlTranslation]);

    const cycleLangList = (contactCycleLangList || [])
        .map(ccl => ccl.cycle_lang)
        .filter(cycleLang => cycleLang && typeof cycleLang === 'object');

    const vertical = cycleLangList.find(cycleLang => cycleLang.cycle_lang_type === 'collectif_planaction');
    const verticalLogo = vertical?.attachedFilesCollection?.find(file => file.id_attached_file_type == 65)?.small;

    const collectifsGeo = [...new Map(
        cycleLangList
            .filter(cycleLang => cycleLang.cycle_lang_type === 'collectif_geographique')
            .map(cycleLang => [cycleLang.id_cycle_lang, cycleLang])
    ).values()];

    // Pays ciblés : union dédupliquée des collectifs géo de catégorie « pays » et des
    // enfants « pays » des collectifs continents (extension de la logique PHP targetedCountries)
    const paysCibles = new Set();
    collectifsGeo.forEach(cycleLang => {
        if (cycleLang.cycle_lang_categorie === 'pays') paysCibles.add(cycleLang.id_cycle_lang);
        (cycleLang.children || []).forEach(child => {
            if (child.cycle_lang_categorie === 'pays') paysCibles.add(child.id_cycle_lang);
        });
    });
    const numTargetedCountries = paysCibles.size || collectifsGeo.length;

    // Coordinateur du collectif actif (planaction en priorité, sinon géographique) :
    // membres du collectif dont le event_contact_type est un rôle de coordination
    // (383 Coordinateur VERTICAL en priorité, sinon tout libellé « coordinateur »)
    useEffect(() => {
        const idCycleLangCible = vertical?.id_cycle_lang || collectifsGeo[0]?.id_cycle_lang;
        if (!idCycleLangCible) return;
        const controller = new AbortController();

        fetch(`${apiUrlTranslation}?action=getContactCycleLang&filter=id_cycle_lang=${idCycleLangCible}&get_parents=0&get_children=0&source=contactprofilebanner`, { signal: controller.signal })
            .then(res => res.json())
            .then(res => {
                const membres = (Array.isArray(res) ? res : []).filter(ccl => ccl.contact);
                const coordinateurs = membres.filter(ccl => /coordinateur/i.test(ccl.event_contact_type?.libelle || ''));
                const verticalCoord = coordinateurs.find(ccl => ccl.id_event_contact_type == 383);
                setCoordinateur((verticalCoord || coordinateurs[0])?.contact || null);
            })
            .catch(err => { if (err.name !== 'AbortError') console.error(err); });

        return () => controller.abort();
    }, [vertical?.id_cycle_lang, collectifsGeo.length, apiUrlTranslation, i18n.resolvedLanguage]);

    if (!contact) return null;

    return (
        <div
            className="contact-profile-banner rounded-2xl text-white grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 my-4 overflow-hidden"
            style={{ background: 'linear-gradient(160deg,#052e1a 0%,#0b4a2d 50%,#062d1b 100%)' }}>

            {vertical && <BannerBlock label="Vertical">
                {verticalLogo
                    ? <img src={verticalLogo} alt={vertical.nom_cycle_lang} className="max-h-16 object-contain" />
                    : <span className="text-sm font-bold text-center">{vertical.nom_cycle_lang}</span>}
            </BannerBlock>}

            {contact.pays && <BannerBlock label="Country based">
                {/* contact.flag = URL d'image ou emoji drapeau selon le backend */}
                {contact.flag && (String(contact.flag).startsWith('http')
                    ? <img src={contact.flag} alt={contact.pays} className="w-14 h-14 rounded-full object-cover" />
                    : <span className="text-5xl leading-none">{contact.flag}</span>)}
                <span className="text-xs font-bold uppercase text-center">{contact.pays}</span>
            </BannerBlock>}

            {numTargetedCountries > 0 && <BannerBlock label="Export target">
                <div className="relative w-20 h-20 flex items-center justify-center">
                    <GlobeSvg />
                    <span className="relative text-3xl font-black leading-none">{numTargetedCountries}</span>
                </div>
                <span className="text-xs font-bold tracking-widest uppercase">Countries</span>
            </BannerBlock>}

            {coordinateur && <BannerBlock label="Coordinated by">
                <img
                    src={coordinateur.photos?.small}
                    alt={`${coordinateur.prenom} ${coordinateur.nom}`}
                    className="w-14 h-14 rounded-full object-cover border-2 border-white/40" />
                <span className="text-xs font-bold uppercase text-yellow-300 text-center">{coordinateur.prenom} {coordinateur.nom}</span>
            </BannerBlock>}

            {cohort && <BannerBlock label="Cohort">
                {cohort.image && <img src={cohort.image} alt={cohort.label} className="max-h-16 rounded object-cover" />}
                <span className="text-sm font-black uppercase text-yellow-300 text-center leading-tight">{cohort.label}</span>
            </BannerBlock>}

            <BannerBlock label="Export potential">
                <div className="relative w-20 h-20 flex items-center justify-center">
                    <LevelBadgeSvg />
                    <div className="relative flex flex-col items-center leading-none">
                        <span className="text-[9px] font-bold tracking-widest uppercase">Level</span>
                        <span className="text-3xl font-black">{exportLevel + 1}</span>
                    </div>
                </div>
            </BannerBlock>

        </div>
    );
};

export default ContactProfileBanner;
